import { Resource } from 'react-admin'
import { Permissions } from '../types/apiTypes'
import { SCOPES } from '../constants/scopes'
import { SHOW_PROXIES } from '../utils/featureFlags'
import applications from './Application'
import auditLogs from './AuditLog'
import requests from './Requests'
import automationRules from './AutomationRule'
import mcpServers from './McpServer'
import mcpTools from './McpTool'
import monitoringAlerts from './MonitoringAlert'
import organizations from './Organization'
import organizationUsers from './OrganizationUser'
import organizationMcpLinks from './OrganizationMcpLink'
import organizationProxyLinks from './OrganizationProxyLink'
import proxies from './Proxy'
import proxyTools from './ProxyTool'
import organizationWorkflowLinks from './OrganizationWorkflowLink'
import workflowExecutions from './WorkflowExecution'
import workflows from './Workflow'

interface ResourceOptions {
  label?: string
  scopes: string[]
  submenu?: string
}

const availableResources = ({scopes}: Permissions): JSX.Element[] => [
  <Resource
    key="application"
    name="application"
    options={{ label: 'menu.applications', scopes: [SCOPES.APPLICATION_READ], submenu: 'beheer' }}
    list={applications.list}
    create={applications.create}
    show={applications.show}
    edit={applications.edit}
    icon={applications.icon}
  />,
  <Resource
    key="mcp_server"
    name="mcp_server"
    options={{ label: 'menu.mcpServers', scopes: [SCOPES.MCP_READ] }}
    list={mcpServers.list}
    create={mcpServers.create}
    show={mcpServers.show}
    edit={mcpServers.edit}
    icon={mcpServers.icon}
  />,
  <Resource
    key="workflow"
    name="workflow"
    options={{ label: 'menu.workflows', scopes: [SCOPES.MCP_READ] }}
    list={workflows.list}
    create={workflows.create}
    show={workflows.show}
    edit={workflows.edit}
    icon={workflows.icon}
  />,
  <Resource
    key="proxy"
    name="proxy"
    options={{ label: 'menu.proxies', scopes: [SCOPES.PROXY_READ] }}
    list={proxies.list}
    create={proxies.create}
    show={proxies.show}
    edit={proxies.edit}
    icon={proxies.icon}
  />,
  <Resource
    key="workflow_execution"
    name="workflow_execution"
    options={{ scopes: [SCOPES.MCP_READ] }}
    list={workflowExecutions.list}
    show={workflowExecutions.show}
  />,
  <Resource
    key="requests"
    name="requests"
    options={{ label: 'menu.requests', scopes: [SCOPES.ACCESS_REQUEST_READ, SCOPES.PLATFORM_REQUEST_READ] }}
    list={requests.list}
    icon={requests.icon}
  />,
  <Resource
    key="mcp_tool"
    name="mcp_tool"
    options={{ scopes: [SCOPES.MCP_READ] }}
    create={mcpTools.create}
    edit={mcpTools.edit}
  />,
  <Resource
    key="proxy_tool"
    name="proxy_tool"
    options={{ scopes: [SCOPES.PROXY_READ] }}
    create={proxyTools.create}
    edit={proxyTools.edit}
  />,
  <Resource key="tool_source" name="tool_source" options={{ scopes: [SCOPES.MCP_READ] }} />,
  <Resource
    key="organization"
    name="organization"
    options={{ label: 'menu.organizations', scopes: [SCOPES.ORGANIZATION_READ], submenu: 'beheer' }}
    list={organizations.list}
    show={organizations.show}
    edit={organizations.edit}
    icon={organizations.icon}
  />,
  <Resource
    key="organization_user"
    name="organization_user"
    options={{ scopes: [SCOPES.ORGANIZATION_READ] }}
    create={organizationUsers.create}
    edit={organizationUsers.edit}
  />,
  <Resource
    key="organization_mcp_link"
    name="organization_mcp_link"
    options={{ scopes: [SCOPES.ORG_LINK_READ] }}
    create={organizationMcpLinks.create}
    edit={organizationMcpLinks.edit}
  />,
  <Resource
    key="organization_proxy_link"
    name="organization_proxy_link"
    options={{ scopes: [SCOPES.ORG_LINK_READ] }}
    create={organizationProxyLinks.create}
    edit={organizationProxyLinks.edit}
  />,
  <Resource
    key="organization_workflow_link"
    name="organization_workflow_link"
    options={{ scopes: [SCOPES.ORG_LINK_READ] }}
    create={organizationWorkflowLinks.create}
    edit={organizationWorkflowLinks.edit}
  />,
  <Resource
    key="mcp_link_user"
    name="mcp_link_user"
    options={{ scopes: [SCOPES.ORG_LINK_READ] }}
  />,
  <Resource
    key="mcp_usage_log"
    name="mcp_usage_log"
    options={{ scopes: [SCOPES.DASHBOARD_READ] }}
  />,
  <Resource
    key="proxy_usage_log"
    name="proxy_usage_log"
    options={{ scopes: [SCOPES.DASHBOARD_READ] }}
  />,
  <Resource
    key="automation_rule"
    name="automation_rule"
    options={{ label: 'menu.automation', scopes: [SCOPES.MONITORING_READ], submenu: 'beheer' }}
    list={automationRules.list}
    create={automationRules.create}
    show={automationRules.show}
    edit={automationRules.edit}
    icon={automationRules.icon}
  />,
  <Resource
    key="monitoring_alert"
    name="monitoring_alert"
    options={{ label: 'menu.alerts', scopes: [SCOPES.MONITORING_READ], submenu: 'beheer' }}
    list={monitoringAlerts.list}
    show={monitoringAlerts.show}
    icon={monitoringAlerts.icon}
  />,
  <Resource
    key="audit_log"
    name="audit_log"
    options={{ label: 'menu.auditLog', scopes: [SCOPES.DASHBOARD_READ], submenu: 'beheer' }}
    list={auditLogs.list}
    icon={auditLogs.icon}
  />,
  // <Resource
  //   key="document_upload"
  //   name="document_upload"
  //   options={{ label: 'Documents', scopes: [SCOPES.DASHBOARD_READ] }}
  //   list={documentUploads.list}
  //   show={documentUploads.show}
  //   icon={documentUploads.icon}
  // />,
  <Resource
    key="access_request"
    name="access_request"
    options={{ scopes: [SCOPES.ACCESS_REQUEST_READ] }}
  />,
  <Resource
    key="platform_request"
    name="platform_request"
    options={{ scopes: [SCOPES.PLATFORM_REQUEST_READ] }}
  />,
  <Resource
    key="org_openwebui_config"
    name="org_openwebui_config"
    options={{ scopes: [SCOPES.ORGANIZATION_READ] }}
  />,
  <Resource
    key="portal_settings"
    name="portal_settings"
    options={{ scopes: [SCOPES.ORGANIZATION_READ] }}
  />,
  <Resource
    key="application_header"
    name="application_header"
    options={{ scopes: [SCOPES.APPLICATION_READ] }}
  />,
  <Resource
    key="application_environment"
    name="application_environment"
    options={{ scopes: [SCOPES.APPLICATION_READ] }}
  />,
  <Resource
    key="mcp_skill"
    name="mcp_skill"
    options={{ scopes: [SCOPES.MCP_READ] }}
  />,
  <Resource
    key="document_download_log"
    name="document_download_log"
    options={{ scopes: [SCOPES.DASHBOARD_READ] }}
  />,
  <Resource
    key="usage_stats"
    name="usage_stats"
    options={{ scopes: [SCOPES.DASHBOARD_READ] }}
  />,
]

const fallback = (): JSX.Element[] => [
  <Resource
    key="organization"
    name="organization"
    options={{ label: 'menu.organizations' }}
    list={organizations.list}
    icon={organizations.icon}
  />
]

export const resourcesForScopes = ({ scopes }: Permissions): JSX.Element[] => {
  if (!scopes || scopes.length === 0) {
    console.warn('No scopes received, returning fallback resource')
    return fallback()
  }
  const PROXY_RESOURCE_KEYS = new Set([
    'proxy',
    'proxy_tool',
    'organization_proxy_link',
    'proxy_usage_log',
  ])
  const resources = availableResources({ scopes })
    .filter((resource) => {
      if (!SHOW_PROXIES && PROXY_RESOURCE_KEYS.has(resource.key as string)) return false
      const options = (resource.props as { options?: ResourceOptions }).options
      return options?.scopes?.some((scope) => scopes.includes(scope)) ?? false
    })
  if (!resources || resources.length === 0) {
    console.warn('No resources received, returning fallback resource')
    return fallback()
  }
  return resources
}
